import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Search, Pencil, Trash2, Inbox, Download, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { sno: "", district: "", name: "", mtype: "MUNICIPAL COMMITTEE", population: "", properties: "", area: "" };
const TYPES = ["MUNICIPAL CORPORATION", "MUNICIPAL COUNCIL", "MUNICIPAL COMMITTEE"];

export default function StateMunicipalities() {
  const { hasPerm } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [editing, setEditing] = useState(null); // null=closed, {}=new, {id..}=edit
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/municipalities").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return items;
    return items.filter((it) => [it.district, it.name, it.mtype].some((v) => (v || "").toLowerCase().includes(q)));
  }, [items, search]);

  const NUMERIC = ["sno", "population", "properties", "area"];
  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    const arr = [...filtered];
    const numeric = NUMERIC.includes(sort.key);
    arr.sort((a, b) => {
      let av = a[sort.key], bv = b[sort.key];
      if (numeric) {
        av = av == null ? -Infinity : Number(av);
        bv = bv == null ? -Infinity : Number(bv);
        return sort.dir === "asc" ? av - bv : bv - av;
      }
      av = (av ?? "").toString().toLowerCase();
      bv = (bv ?? "").toString().toLowerCase();
      return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return arr;
  }, [filtered, sort]);

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const SortIcon = ({ k }) =>
    sort.key !== k ? <ChevronsUpDown className="h-3.5 w-3.5 text-slate-300" />
      : sort.dir === "asc" ? <ChevronUp className="h-3.5 w-3.5 text-emerald-600" />
      : <ChevronDown className="h-3.5 w-3.5 text-emerald-600" />;

  const exportCsv = () => {
    const headers = ["S.No", "District", "Municipal Name", "Type", "Population", "Properties", "Area (sq.km.)"];
    const rows = sorted.map((it) => [it.sno, it.district, it.name, it.mtype, it.population, it.properties, it.area]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "state_municipalities.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded CSV");
  };

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (it) => {
    setForm({ sno: it.sno ?? "", district: it.district || "", name: it.name || "", mtype: it.mtype || TYPES[2],
      population: it.population ?? "", properties: it.properties ?? "", area: it.area ?? "" });
    setEditing(it);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.district.trim() || !form.name.trim()) { toast.error("District and Municipal Name are required"); return; }
    setSaving(true);
    const payload = {
      sno: form.sno === "" ? null : Number(form.sno),
      district: form.district.trim(),
      name: form.name.trim(),
      mtype: form.mtype,
      population: form.population === "" ? null : Number(form.population),
      properties: form.properties === "" ? null : Number(form.properties),
      area: form.area === "" ? null : Number(form.area),
    };
    try {
      if (editing && editing.id) await api.put(`/municipalities/${editing.id}`, payload);
      else await api.post("/municipalities", payload);
      toast.success(editing && editing.id ? "Updated" : "Added");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/municipalities/${deleteItem.id}`);
      toast.success("Deleted");
      setDeleteItem(null);
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const num = (v) => (v == null || v === "" ? "—" : Number(v).toLocaleString("en-IN"));

  return (
    <div data-testid="state-municipalities-page">
      <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-600 font-medium">Master Data</p>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">State Municipalities</h1>
          <p className="text-sm text-slate-500 mt-1">{items.length} municipalities across Haryana</p>
        </div>
        <div className="flex items-center gap-2">
          {hasPerm("municipalities.export") && (
            <Button variant="outline" onClick={exportCsv} className="border-slate-300" data-testid="muni-export-btn">
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
          )}
          {hasPerm("municipalities.manage") && (
            <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="muni-add-btn">
              <Plus className="h-4 w-4 mr-2" /> Add New
            </Button>
          )}
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <div className="p-4 border-b border-slate-200">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by district, name or type..." className="pl-9" data-testid="muni-search-input" />
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-14 cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSort("sno")} data-testid="muni-sort-sno">
                  <span className="inline-flex items-center gap-1">S.No <SortIcon k="sno" /></span>
                </TableHead>
                <TableHead className="cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSort("district")} data-testid="muni-sort-district">
                  <span className="inline-flex items-center gap-1">District <SortIcon k="district" /></span>
                </TableHead>
                <TableHead className="cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSort("name")} data-testid="muni-sort-name">
                  <span className="inline-flex items-center gap-1">Municipal Name <SortIcon k="name" /></span>
                </TableHead>
                <TableHead className="cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSort("mtype")} data-testid="muni-sort-mtype">
                  <span className="inline-flex items-center gap-1">Type <SortIcon k="mtype" /></span>
                </TableHead>
                <TableHead className="text-right cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSort("population")} data-testid="muni-sort-population">
                  <span className="inline-flex items-center gap-1 justify-end">Population <SortIcon k="population" /></span>
                </TableHead>
                <TableHead className="text-right cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSort("properties")} data-testid="muni-sort-properties">
                  <span className="inline-flex items-center gap-1 justify-end">Properties <SortIcon k="properties" /></span>
                </TableHead>
                <TableHead className="text-right cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSort("area")} data-testid="muni-sort-area">
                  <span className="inline-flex items-center gap-1 justify-end">Area (sq.km.) <SortIcon k="area" /></span>
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">Loading...</TableCell></TableRow>
              ) : sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12">
                    <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">No municipalities found.</p>
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((it) => (
                  <TableRow key={it.id} className="hover:bg-slate-50" data-testid={`muni-row-${it.id}`}>
                    <TableCell className="text-slate-500">{it.sno ?? "—"}</TableCell>
                    <TableCell className="capitalize">{it.district?.toLowerCase()}</TableCell>
                    <TableCell className="font-medium">{it.name}</TableCell>
                    <TableCell className="text-slate-600 text-xs">{it.mtype}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(it.population)}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(it.properties)}</TableCell>
                    <TableCell className="text-right tabular-nums">{it.area == null ? "—" : it.area}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {hasPerm("municipalities.manage") ? (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(it)} data-testid={`muni-edit-${it.id}`}><Pencil className="h-4 w-4 text-slate-600" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteItem(it)} data-testid={`muni-delete-${it.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </>
                      ) : <span className="text-xs text-slate-400">—</span>}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="muni-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing && editing.id ? "Edit Municipality" : "Add Municipality"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5 col-span-2">
              <Label>District *</Label>
              <Input value={form.district} onChange={(e) => setField("district", e.target.value)} data-testid="muni-input-district" />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>Municipal Name *</Label>
              <Input value={form.name} onChange={(e) => setField("name", e.target.value)} data-testid="muni-input-name" />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>Municipal Type</Label>
              <select value={form.mtype} onChange={(e) => setField("mtype", e.target.value)} className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="muni-input-mtype">
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Population</Label>
              <Input type="number" value={form.population} onChange={(e) => setField("population", e.target.value)} data-testid="muni-input-population" />
            </div>
            <div className="space-y-1.5">
              <Label>No. of Properties</Label>
              <Input type="number" value={form.properties} onChange={(e) => setField("properties", e.target.value)} data-testid="muni-input-properties" />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>Area (sq.km.)</Label>
              <Input type="number" step="0.01" value={form.area} onChange={(e) => setField("area", e.target.value)} data-testid="muni-input-area" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="muni-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="muni-save-btn">
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteItem} onOpenChange={(o) => !o && setDeleteItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteItem?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="muni-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600" data-testid="muni-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
